import * as api from '@gitbook/api-client';

import type { PricingFeature } from './table_features';

export enum AIUseCase {
    AdaptiveContentSegments = 'adaptive-content:segments',
    AdaptiveContentTemplateConditions = 'adaptive-content:template-conditions',
    ContentImportEnhanceMarkdown = 'content-import:enhance-markdown',
    ContentIndexingEmbedDocument = 'content-indexing:embed-document',
    DocsAgentsChangeRequest = 'docs-agents:change-request',
    DocsAgentsConversationTitle = 'docs-agents:conversation-title',
    DocsAgentsOrganization = 'docs-agents:organization',
    DocsAgentsOrganizationBackground = 'docs-agents:organization-background',
    InternalSearch = 'internal-search',
    McpAgentFeedbackProcessor = 'mcp:agent-feedback-processor',
    OrganizationAsk = 'organization-ask',
    OrganizationAskSuggestions = 'organization-ask:suggestions',
    OrganizationRecommendedQuestions = 'organization-recommended-questions',
    SiteAPI = 'site-api',
    SiteAPISuggestions = 'site-api:suggestions',
    SiteAsk = 'site-ask',
    SiteAskSuggestions = 'site-ask:suggestions',
    SiteChannels = 'site-channels',
    SiteContextFindingProcessor = 'site-context:finding-processor',
    SiteContextRecordsEmbedRecord = 'site-context-records:embed-record',
    SiteContextRecordsSanitizeBody = 'site-context-records:sanitize-body',
    SiteContextScanFindingsPromoter = 'site-context-scan:findings-promoter',
    SiteContextScanFindingsScanner = 'site-context-scan:findings-scanner',
    SiteContextScanLinkAuditor = 'site-context-scan:link-auditor',
    SiteContextTopicsAnalyseTopic = 'site-context-topics:analyse-topic',
    SiteContextTopicsEmbedTopic = 'site-context-topics:embed-topic',
    SiteContextTopicsExtractTopics = 'site-context-topics:extract-topics',
    SiteContextTopicsMatch = 'site-context-topics:match',
    SiteQuestionsAnalyseAnswer = 'site-questions:analyse-answer',
    SiteQuestionsEmbedQuestion = 'site-questions:embed-question',
    SiteQuestionsMergeQuestion = 'site-questions:merge-question',
    SiteRecommendedQuestions = 'site-recommended-questions',
    Translation = 'translation',
}

/** Customer-facing categories for AI usage. */
export enum AIFeature {
    Agent = 'ai-agent',
    Assistant = 'ai-assistant',
    BrokenLinks = 'ai-broken-links',
    ContentGap = 'ai-content-gap',
    Insight = 'ai-insight',
    Import = 'ai-import',
    StyleGuide = 'ai-style-guide',
    Translation = 'ai-translation',

    /**
     * AI work that backs other features rather than being sold on its own (content embeddings,
     * adaptive content suggestions). It is an exception in how AI is gated: `shouldRunAIFeature`
     * stops it only when an administrator turns AI off, so it keeps running where the plan lacks
     * AI or AI is paused, letting AI work as soon as it becomes available and be previewed before
     * an upgrade. See `isAIPlatformPricingFeature`.
     */
    Platform = 'ai-platform',
}

/**
 * AI settings whose changes require refreshing published-site caches.
 */
export const AI_FEATURES_AFFECTING_PUBLISHED_CONTENT: readonly AIFeature[] = [AIFeature.Assistant];

export interface AICreditBundle {
    lookupKey: string;
    name: string;
    monthlyAmountCents: number;
    paidCredits: number;
    freeCredits: number;
}

export enum AIModelId {
    GPT6Astra = 'gpt-6-astra',
    GPT6Luna = 'gpt-6-luna',
    GPT56Sol = 'gpt-5.6-sol',
    GPT56Terra = 'gpt-5.6-terra',
    GPT56Luna = 'gpt-5.6-luna',
    GPT55 = 'gpt-5.5',
    GPT55Pro = 'gpt-5.5-pro',
    GPT54 = 'gpt-5.4',
    GPT54Mini = 'gpt-5.4-mini',
    GPT54Pro = 'gpt-5.4-pro',
    GPT52 = 'gpt-5.2',
    GPT5Nano = 'gpt-5-nano',
}

export enum AIEmbeddingModelId {
    TextEmbedding3Large = 'text-embedding-3-large',
}

export enum AICreditPriceLookupKey {
    Overage = 'ai_credits_metered_monthly',
    Unlimited = 'ai_credits_unlimited_monthly',
}

export const AI_CREDIT_UNIT_USD = 0.01;
export const AI_CREDIT_CURRENCY = 'usd';
export const AI_CREDITS_METER_EVENT_NAME = 'ai_credits';

/**
 * This metadata allows us to replace a Stripe price using `transfer_lookup_key=true`
 * while still identifying the old price on existing subscriptions.
 */
export const AI_CREDIT_LOOKUP_KEY_METADATA_KEY = 'ai_credit_lookup_key';
export const DEFAULT_AI_CREDIT_ROLLOVER = 50;

/**
 * Defines consumption priorities and Stripe categories for managed AI credit grants.
 * Custom grants use the category configured in Stripe.
 *
 * Stripe applies the lowest priority number first. The default consumption order is
 * site → bonus → rollover → custom → paid. Paid credits are burned last so that unused ones survive
 * the period and can roll over.
 */
export const AI_CREDIT_GRANTS = {
    [api.AICreditGrantType.Site]: {
        category: 'promotional',
        priority: 10,
    },
    [api.AICreditGrantType.Paid]: {
        category: 'paid',
        priority: 100,
    },
    [api.AICreditGrantType.Bonus]: {
        category: 'promotional',
        priority: 20,
    },
    [api.AICreditGrantType.Rollover]: {
        category: 'paid',
        priority: 30,
    },
    [api.AICreditGrantType.Custom]: {
        priority: 50,
    },
} as const satisfies Record<
    api.AICreditGrantType,
    {
        category?: 'paid' | 'promotional';
        priority: number;
    }
>;

/** Defines how many credits are granted by each site type. */
export const AI_CREDITS_BY_SITE_TYPE: Record<api.SiteType, number> = {
    [api.SiteType.Basic]: 0,
    [api.SiteType.Premium]: 2_000,
    [api.SiteType.Ultimate]: 10_000,
    [api.SiteType.Sponsored]: 0,
};

/**
 * Stripe lookup keys identify the current price for each bundle across environments.
 * When replacing a price, assign its lookup key to the new price with `transfer_lookup_key=true`.
 * Existing subscriptions keep the old price while new selections resolve to the replacement.
 */
export const AI_CREDIT_BUNDLES = {
    [api.BillingCreditsBundle.None]: {
        lookupKey: AICreditPriceLookupKey.Overage,
        name: 'AI credits',
        monthlyAmountCents: 0,
        paidCredits: 0,
        freeCredits: 0,
    },
    [api.BillingCreditsBundle.Value4750250]: {
        lookupKey: 'ai_credits_4750_250',
        name: '5,000 AI credits',
        monthlyAmountCents: 4_750,
        paidCredits: 4_750,
        freeCredits: 250,
    },
    [api.BillingCreditsBundle.Value90001000]: {
        lookupKey: 'ai_credits_9000_1000',
        name: '10,000 AI credits',
        monthlyAmountCents: 9_000,
        paidCredits: 9_000,
        freeCredits: 1_000,
    },
    [api.BillingCreditsBundle.Value127502250]: {
        lookupKey: 'ai_credits_12750_2250',
        name: '15,000 AI credits',
        monthlyAmountCents: 12_750,
        paidCredits: 12_750,
        freeCredits: 2_250,
    },
    [api.BillingCreditsBundle.Value3000010000]: {
        lookupKey: 'ai_credits_30000_10000',
        name: '40,000 AI credits',
        monthlyAmountCents: 30_000,
        paidCredits: 30_000,
        freeCredits: 10_000,
    },
} satisfies Record<api.BillingCreditsBundle, AICreditBundle>;

/** Allowed pricing features and reporting categories for every AI use case. */
export const AI_FEATURE_ATTRIBUTIONS = {
    [AIUseCase.AdaptiveContentSegments]: {
        [api.SiteFeatureKey.SitesAdaptiveContent]: AIFeature.Platform,
    },
    [AIUseCase.AdaptiveContentTemplateConditions]: {
        [api.SiteFeatureKey.SitesAdaptiveContent]: AIFeature.Platform,
    },
    [AIUseCase.ContentImportEnhanceMarkdown]: {
        [api.OrganizationFeature.AiWriting]: AIFeature.Import,
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
        [api.SiteFeatureKey.SitesFixBrokenLinks]: AIFeature.BrokenLinks,
        [api.SiteFeatureKey.SitesStyleGuide]: AIFeature.StyleGuide,
        [api.SiteFeatureKey.SitesChannels]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesConnections]: AIFeature.Agent,
    },
    [AIUseCase.ContentIndexingEmbedDocument]: {
        [api.OrganizationFeature.Ai]: AIFeature.Platform,
    },
    [AIUseCase.DocsAgentsChangeRequest]: {
        [api.OrganizationFeature.AiWriting]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
        [api.SiteFeatureKey.SitesFixBrokenLinks]: AIFeature.BrokenLinks,
        [api.SiteFeatureKey.SitesStyleGuide]: AIFeature.StyleGuide,
        [api.SiteFeatureKey.SitesChannels]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesConnections]: AIFeature.Agent,
    },
    [AIUseCase.DocsAgentsConversationTitle]: {
        [api.OrganizationFeature.AiWriting]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
        [api.SiteFeatureKey.SitesFixBrokenLinks]: AIFeature.BrokenLinks,
        [api.SiteFeatureKey.SitesChannels]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesConnections]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesStyleGuide]: AIFeature.StyleGuide,
    },
    [AIUseCase.DocsAgentsOrganization]: {
        [api.OrganizationFeature.AiWriting]: AIFeature.Agent,
    },
    [AIUseCase.DocsAgentsOrganizationBackground]: {
        [api.OrganizationFeature.AiWriting]: AIFeature.Agent,
    },
    [AIUseCase.InternalSearch]: {
        [api.OrganizationFeature.Ai]: AIFeature.Platform,
    },
    [AIUseCase.McpAgentFeedbackProcessor]: {
        [api.OrganizationFeature.Ai]: AIFeature.Platform,
    },
    [AIUseCase.OrganizationAsk]: {
        [api.OrganizationFeature.AiLens]: AIFeature.Agent,
    },
    [AIUseCase.OrganizationAskSuggestions]: {
        [api.OrganizationFeature.AiLens]: AIFeature.Agent,
    },
    [AIUseCase.OrganizationRecommendedQuestions]: {
        [api.OrganizationFeature.AiLens]: AIFeature.Agent,
    },
    [AIUseCase.SiteAPI]: {
        [api.SiteFeatureKey.SitesAiAssistant]: AIFeature.Assistant,
    },
    [AIUseCase.SiteAPISuggestions]: {
        [api.SiteFeatureKey.SitesAiAssistant]: AIFeature.Assistant,
    },
    [AIUseCase.SiteAsk]: {
        [api.SiteFeatureKey.SitesAiSearch]: AIFeature.Assistant,
    },
    [AIUseCase.SiteAskSuggestions]: {
        [api.SiteFeatureKey.SitesAiSearch]: AIFeature.Assistant,
    },
    [AIUseCase.SiteChannels]: {
        [api.SiteFeatureKey.SitesChannels]: AIFeature.Agent,
    },
    [AIUseCase.SiteContextFindingProcessor]: {
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
        [api.SiteFeatureKey.SitesFixBrokenLinks]: AIFeature.BrokenLinks,
    },
    [AIUseCase.SiteContextRecordsEmbedRecord]: {
        [api.SiteFeatureKey.SitesConnections]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteContextRecordsSanitizeBody]: {
        [api.SiteFeatureKey.SitesConnections]: AIFeature.Agent,
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteContextScanFindingsPromoter]: {
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteContextScanFindingsScanner]: {
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteContextScanLinkAuditor]: {
        [api.SiteFeatureKey.SitesFixBrokenLinks]: AIFeature.BrokenLinks,
    },
    [AIUseCase.SiteContextTopicsAnalyseTopic]: {
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteContextTopicsEmbedTopic]: {
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteContextTopicsExtractTopics]: {
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteContextTopicsMatch]: {
        [api.SiteFeatureKey.SitesContentGaps]: AIFeature.ContentGap,
    },
    [AIUseCase.SiteQuestionsAnalyseAnswer]: {
        [api.SiteFeatureKey.SitesAiInsights]: AIFeature.Insight,
    },
    [AIUseCase.SiteQuestionsEmbedQuestion]: {
        [api.SiteFeatureKey.SitesAiInsights]: AIFeature.Insight,
    },
    [AIUseCase.SiteQuestionsMergeQuestion]: {
        [api.SiteFeatureKey.SitesAiInsights]: AIFeature.Insight,
    },
    [AIUseCase.SiteRecommendedQuestions]: {
        [api.SiteFeatureKey.SitesAiSearch]: AIFeature.Assistant,
    },
    [AIUseCase.Translation]: {
        [api.OrganizationFeature.Translations]: AIFeature.Translation,
    },
} as const satisfies Record<AIUseCase, Partial<Record<PricingFeature, AIFeature>>>;

/** Pricing features supported by a particular AI operation. */
export type AIPricingFeature<U extends AIUseCase = AIUseCase> = {
    [K in U]: keyof (typeof AI_FEATURE_ATTRIBUTIONS)[K];
}[U];

/** Pricing feature that processing a site finding of each type is attributed to. */
export const SITE_FINDING_PRICING_FEATURES = {
    [api.SiteFindingType.ContentGap]: api.SiteFeatureKey.SitesContentGaps,
    [api.SiteFindingType.BrokenLink]: api.SiteFeatureKey.SitesFixBrokenLinks,
    [api.SiteFindingType.OutboundLinkError]: api.SiteFeatureKey.SitesFixBrokenLinks,
    [api.SiteFindingType.ContentOutdated]: api.SiteFeatureKey.SitesContentGaps,
    [api.SiteFindingType.ContentIncorrect]: api.SiteFeatureKey.SitesContentGaps,
    [api.SiteFindingType.Other]: api.SiteFeatureKey.SitesContentGaps,
} as const satisfies Record<
    api.SiteFindingType,
    AIPricingFeature<AIUseCase.SiteContextFindingProcessor>
>;

/** A valid pair, preserving the relationship between use case and pricing feature. */
export type AIAttribution<U extends AIUseCase = AIUseCase> = {
    [K in U]: { useCase: K; pricingFeature: AIPricingFeature<K> };
}[U];

interface AIOutcomePrice {
    credits: number;
    per: number;
}

export const AI_OUTCOME_PRICES = {
    [AIFeature.Agent]: null,
    [AIFeature.Assistant]: {
        'relevant-answers': {
            credits: 20,
            per: 1,
        },
    },
    [AIFeature.BrokenLinks]: null,
    [AIFeature.ContentGap]: null,
    [AIFeature.Insight]: null,
    [AIFeature.Import]: null,
    [AIFeature.Platform]: null,
    [AIFeature.StyleGuide]: null,
    [AIFeature.Translation]: {
        'translated-words': {
            credits: 30,
            per: 1_000,
        },
    },
} as const satisfies Record<AIFeature, Record<string, AIOutcomePrice> | null>;

export type AIOutcomeFeature = {
    [Feature in AIFeature]: (typeof AI_OUTCOME_PRICES)[Feature] extends Record<
        string,
        AIOutcomePrice
    >
        ? Feature
        : never;
}[AIFeature];

/** Catalog-defined pairs whose reporting feature has outcome pricing. */
export type AIOutcomeAttribution = {
    [UseCase in AIUseCase]: {
        [PricingFeatureKey in AIPricingFeature<UseCase>]: (typeof AI_FEATURE_ATTRIBUTIONS)[UseCase][PricingFeatureKey] extends AIOutcomeFeature
            ? {
                  useCase: UseCase;
                  pricingFeature: PricingFeatureKey;
                  metric: keyof (typeof AI_OUTCOME_PRICES)[(typeof AI_FEATURE_ATTRIBUTIONS)[UseCase][PricingFeatureKey]];
              }
            : never;
    }[AIPricingFeature<UseCase>];
}[AIUseCase];

export type AIOutcome = AIOutcomeAttribution & {
    quantity: number;
};

/** Calculate credits for completed application work, independently of model usage. */
export function calculateAIOutcomeCredits(input: AIOutcome): number {
    const prices: Record<string, AIOutcomePrice> = AI_OUTCOME_PRICES[getAIFeature(input)];
    const price = prices[input.metric];
    return Number(((input.quantity * price.credits) / price.per).toFixed(4));
}

/** Enumerate the pricing features declared for an AI operation. */
export function getAIPricingFeatures<U extends AIUseCase>(useCase: U): AIPricingFeature<U>[] {
    return Object.keys(AI_FEATURE_ATTRIBUTIONS[useCase]) as AIPricingFeature<U>[];
}

/**
 * Whether every AI use case billed to the pricing feature is `AIFeature.Platform` work, which is
 * gated differently. A pricing feature mixing Platform and other work gets the stricter gate.
 */
export function isAIPlatformPricingFeature(featureKey: PricingFeature): boolean {
    const attributions: Array<Partial<Record<PricingFeature, AIFeature>>> =
        Object.values(AI_FEATURE_ATTRIBUTIONS);
    const features = attributions.flatMap((attribution) => {
        const feature = attribution[featureKey];
        return feature ? [feature] : [];
    });
    return features.length > 0 && features.every((feature) => feature === AIFeature.Platform);
}

/** Read the category of a supported attribution from the catalog. */
export function getAIFeature(attribution: AIOutcomeAttribution): AIOutcomeFeature;
export function getAIFeature(attribution: AIAttribution): AIFeature;
export function getAIFeature(attribution: AIAttribution): AIFeature {
    return getAIFeatureForUseCase(attribution.useCase, attribution.pricingFeature);
}

/**
 * Whether a credit bundle key is set. BillingCreditsBundle.None counts as credit pricing;
 * null and undefined do not. Credit-pricing orgs without a purchased bundle,
 * including trialing and Free orgs, use BillingCreditsBundle.None.
 */
export function checkIsAICreditPricing(
    creditsBundle: api.BillingCreditsBundle | null | undefined
): boolean {
    return creditsBundle !== null && creditsBundle !== undefined;
}

/** Round credits to the whole number they are displayed as. */
export function roundCredits(credits: number): number {
    return Math.round(credits);
}

export function formatCredits(credits: number): string {
    return roundCredits(credits).toLocaleString();
}

/** Earliest selectable AI usage day: two calendar years ago, at UTC midnight. */
export function getAIUsageMinDate(now: number): Date {
    const date = new Date(now);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCFullYear(date.getUTCFullYear() - 2);
    return date;
}

/**
 * The monthly period containing `now`, falling on the same day of the month as `anchorDate`, a
 * subscription's start or renewal date. A day past the end of a shorter month falls on its last
 * day.
 */
export function getAICreditAllowancePeriod(
    anchorDate: Date,
    now: Date
): { periodStart: Date; periodEnd: Date } {
    let months =
        (now.getUTCFullYear() - anchorDate.getUTCFullYear()) * 12 +
        (now.getUTCMonth() - anchorDate.getUTCMonth());
    if (addUTCMonths(anchorDate, months) > now) {
        months -= 1;
    }

    return {
        periodStart: addUTCMonths(anchorDate, months),
        periodEnd: addUTCMonths(anchorDate, months + 1),
    };
}

function getAIFeatureForUseCase<U extends AIUseCase>(
    useCase: U,
    pricingFeature: AIPricingFeature<U>
): AIFeature {
    const attributions: { [K in AIUseCase]: Record<AIPricingFeature<K>, AIFeature> } =
        AI_FEATURE_ATTRIBUTIONS;
    return attributions[useCase][pricingFeature];
}

function addUTCMonths(date: Date, months: number): Date {
    const result = new Date(date);
    result.setUTCDate(1);
    result.setUTCMonth(result.getUTCMonth() + months);
    const lastDay = new Date(
        Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)
    ).getUTCDate();
    result.setUTCDate(Math.min(date.getUTCDate(), lastDay));
    return result;
}
